/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { sleep } from "common/utils/helpers/delay";
import { mergeImages } from "./utils";
// eslint-disable-next-line import/no-cycle
import { dtfStore } from "./store";

const STICKER_IMAGES_CACHE_NAME = "sticker-images-cache";
const MAX_CACHE_ITEMS = 240;

function retry(fn: () => Promise<unknown>, retries = 10, interval = 1000) {
  const perform = async (retryCount: number, retryInterval: number) => {
    try {
      return await fn();
    } catch (error) {
      if (retryCount <= 0) throw error;
      await sleep(retryInterval);
      return perform(retryCount - 1, retryInterval * 2);
    }
  };

  return perform(retries, interval);
}

async function manageCacheSize() {
  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);
  const keys = await cache.keys();

  if (keys.length > MAX_CACHE_ITEMS && keys[0]) {
    await cache.delete(keys[0]);
    await manageCacheSize();
  }
}

// Top-level keys of the sticker-cutlines response that hold {paddingSize: url} maps. PO-3672
// adds a sibling `with_bg_ratios` ({paddingSize: number}) which must be skipped by URL iterators.
const CUTLINE_BG_STYLES = ["with_bg", "no_bg"];

function extractUrlsByBgStyleAndPadding(cutlineData: Record<string, Record<string, string>>) {
  const urls: Record<string, string> = {};
  Object.entries(cutlineData).forEach(([bgStyle, sizes]) => {
    if (!CUTLINE_BG_STYLES.includes(bgStyle)) return;
    Object.entries(sizes).forEach(([paddingSize, url]) => {
      if (url) {
        const key = `${bgStyle}-${paddingSize}`;
        urls[key] = url;
      }
    });
  });
  return urls;
}

async function cacheCutlineImages(
  data: { artwork: { id: number | string }; shape: string },
  cutlineData: Record<string, Record<string, string>>
) {
  const { artwork, shape } = data;

  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);
  const cacheKey = `${artwork.id}-${shape}`;

  const urls = extractUrlsByBgStyleAndPadding(cutlineData);

  await Promise.all(
    Object.entries(urls).map(async ([key, url]) => {
      const response = await fetch(url);
      if (response.ok) {
        await cache.put(`${cacheKey}-${key}`, response.clone());
      }
    })
  );

  await manageCacheSize();
}

async function getCachedCutlineImage(
  artwork: { id: number | string; removeBackground: boolean },
  shape: string,
  paddingSize: string
) {
  const { id: artworkId, removeBackground } = artwork;
  const bgStyle = removeBackground ? "no_bg" : "with_bg";

  const cacheKey = `${artworkId}-${shape}-${bgStyle}-${paddingSize}`;
  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);

  const cachedResponse = await cache.match(cacheKey);

  if (cachedResponse) return cachedResponse;

  throw new Error(`Image not found in cache for: ${cacheKey}`);
}

let storeInstance: ReturnType<typeof createStickersStore> | null = null;

function createStickersStore() {
  type CutlinesByArtwork = Record<string, Record<string, Record<string, Record<string, string>>>>;

  let isLoading = false;
  const cutlines: CutlinesByArtwork = {};

  const setStickerCutlines = ({
    artwork,
    shape,
    cutlines: cutlinesData
  }: {
    artwork: { id: string | number };
    shape: string;
    cutlines: Record<string, Record<string, string>>;
  }) => {
    const { id: artworkId } = artwork;
    if (!cutlines[artworkId]) {
      cutlines[artworkId] = {};
    }
    cutlines[artworkId][shape] = cutlinesData;
  };

  const setIsLoading = (newValue: boolean) => {
    isLoading = newValue;
  };

  const fetchCutlineUrl = async ({
    artwork,
    shape,
    paddingSize,
    removeBackground,
    useCache = true
  }: {
    artwork: { id: number | string; shape?: string; removeBackground: boolean };
    shape: string;
    paddingSize: string;
    removeBackground: boolean;
    useCache?: boolean;
  }): Promise<{ url: string | null | undefined; ratio: number | undefined }> => {
    const bgStyle = removeBackground ? "no_bg" : "with_bg";

    const artworkCutlines = cutlines[artwork.id]?.[shape];

    // PO-3672: the OFF (with_bg) cutline ratio is per padding size, so resolve it for the
    // requested paddingSize — not the artwork's default — so changing border thickness scales
    // the cutline correctly. ON (no_bg) never has a ratio.
    const ratioFor = (data: { with_bg_ratios?: Record<string, number> } | undefined) =>
      bgStyle === "with_bg" ? data?.with_bg_ratios?.[paddingSize] : undefined;

    if (useCache && artworkCutlines && artworkCutlines[bgStyle]?.[paddingSize]) {
      try {
        // Check if the cutline URL is cached
        await getCachedCutlineImage(artwork, shape, paddingSize);
        return { url: artworkCutlines[bgStyle][paddingSize], ratio: ratioFor(artworkCutlines) };
      } catch {
        // Cache miss — fall through to refetch
      }
    }

    if (artwork.shape !== shape && artworkCutlines) return { url: null, ratio: undefined };

    const cutlinesData = (await retry(() => api.dtf.stickerCutlines({ id: artwork.id }))) as Record<
      string,
      Record<string, string>
    > & { with_bg_ratios?: Record<string, number> };

    await cacheCutlineImages({ artwork, shape }, cutlinesData);

    setStickerCutlines({ artwork, shape, cutlines: cutlinesData });

    return { url: cutlinesData?.[bgStyle]?.[paddingSize], ratio: ratioFor(cutlinesData) };
  };

  // eslint-disable-next-line max-lines-per-function
  const generateStickerImage = async ({
    artwork,
    shape = artwork.shape,
    paddingSize = artwork.paddingSize,
    bgColor = artwork.bgColor,
    removeBackground = artwork.removeBackground,
    useCache = true
  }: {
    artwork: Record<string, unknown> & {
      id: number | string;
      shape: string;
      paddingSize: string;
      bgColor: string;
      removeBackground: boolean;
      url: string;
      stickerCutlineNoBgUrl: string;
      originalNoBgUrl: string;
      stickerCutlineWithBgUrl: string;
      originalImageUrl: string;
      stickerCutlineWithBgRatio?: number;
    };
    shape?: string;
    paddingSize?: string;
    bgColor?: string;
    removeBackground?: boolean;
    useCache?: boolean;
  }) => {
    try {
      setIsLoading(true);
      (dtfStore["toggleLoading"] as (...args: unknown[]) => void)(["removeBg", true]);
      (dtfStore["updateArtworkValuesById"] as (params: Record<string, unknown>) => void)({
        id: artwork.id,
        url: artwork.url,
        processingImage: true
      });

      const { url: cutlineUrl, ratio: fetchedRatio } = await fetchCutlineUrl({
        artwork,
        shape,
        paddingSize,
        removeBackground,
        useCache
      });

      const urls = removeBackground
        ? [artwork.stickerCutlineNoBgUrl, artwork.originalNoBgUrl]
        : [artwork.stickerCutlineWithBgUrl, artwork.originalImageUrl];

      if (cutlineUrl) urls[0] = cutlineUrl;

      // PO-3672: only the background-removal-OFF preview scales the cutline to the artwork,
      // using the per-padding ratio the cutlines endpoint returns (fetchedRatio) as the single
      // authoritative source. When the flip is off the endpoint omits ratios, so fetchedRatio
      // is undefined and the OFF preview reverts cleanly to today's behavior — no sticky scaling
      // from a value cached while the flip was on. The ON path never passes cutlineRatio.
      const cutlineRatio = !removeBackground ? fetchedRatio : undefined;

      const previewUrl = await mergeImages({
        urls,
        bgColor,
        ...(cutlineRatio ? { cutlineRatio } : {})
      });

      (dtfStore["updateArtworkValuesById"] as (params: Record<string, unknown>) => void)({
        id: artwork.id,
        url: previewUrl
      });
    } finally {
      setIsLoading(false);
      (dtfStore["toggleLoading"] as (...args: unknown[]) => void)(["removeBg", false]);
      (dtfStore["updateArtworkValuesById"] as (params: Record<string, unknown>) => void)({
        id: artwork.id,
        processingImage: false
      });
    }
  };

  return {
    isLoading,
    cutlines,
    setStickerCutlines,
    setIsLoading,
    fetchCutlineUrl,
    generateStickerImage
  };
}

export function useStickersStore() {
  if (!storeInstance) storeInstance = createStickersStore();
  return storeInstance;
}
